import React, { useState, useRef, useEffect } from 'react';
import { Documento, TipoDocumento } from '@/types/presupuesto';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Upload } from 'lucide-react';

interface DocumentoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: {
    file?: File;
    nombreCustom: string;
    tipo: TipoDocumento;
  }) => Promise<void>;
  documentoEditar?: Documento | null;
  isLoading?: boolean;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export const DocumentoDialog: React.FC<DocumentoDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  documentoEditar,
  isLoading = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [nombreCustom, setNombreCustom] = useState('');
  const [tipo, setTipo] = useState<TipoDocumento>('proforma');
  const [error, setError] = useState('');

  const isEditMode = !!documentoEditar;

  // Inicializar valores cuando se abre el dialog para editar
  useEffect(() => {
    if (isOpen) {
      if (documentoEditar) {
        setNombreCustom(documentoEditar.nombreCustom);
        setTipo(documentoEditar.tipo);
        setFile(null);
      } else {
        setNombreCustom('');
        setTipo('proforma');
        setFile(null);
      }
      setError('');
    }
  }, [isOpen, documentoEditar]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    const selectedFile = e.target.files?.[0];

    if (!selectedFile) {
      setFile(null);
      return;
    }

    // Validar tamaño
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError(
        `El archivo excede el tamaño máximo permitido (${MAX_FILE_SIZE / 1024 / 1024}MB)`
      );
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setFile(selectedFile);
  };

  const handleConfirm = async () => {
    setError('');

    // Validar que el nombre no esté vacío
    if (!nombreCustom.trim()) {
      setError('El nombre del documento es obligatorio');
      return;
    }

    // Validar que haya archivo en modo crear
    if (!isEditMode && !file) {
      setError('Debe seleccionar un archivo');
      return;
    }

    try {
      await onConfirm({
        file: file || undefined,
        nombreCustom: nombreCustom.trim(),
        tipo,
      });
      onClose();
    } catch (err) {
      setError('Error al guardar el documento');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Editar Documento' : 'Agregar Documento'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Selector de archivo (solo en modo crear) */}
          {!isEditMode && (
            <div className="space-y-2">
              <Label htmlFor="file">Archivo *</Label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  id="file"
                  type="file"
                  onChange={handleFileChange}
                  disabled={isLoading}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLoading}
                  className="w-full min-w-0 justify-start"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  <span className="truncate">{file ? file.name : 'Seleccionar archivo'}</span>
                </Button>
              </div>
              {file && (
                <p className="text-xs text-muted-foreground">
                  Tamaño: {(file.size / 1024).toFixed(1)} KB
                </p>
              )}
            </div>
          )}

          {/* Nombre personalizado */}
          <div className="space-y-2">
            <Label htmlFor="nombreCustom">Nombre del documento *</Label>
            <Input
              id="nombreCustom"
              value={nombreCustom}
              onChange={(e) => setNombreCustom(e.target.value)}
              placeholder="Ej: Presupuesto Inicial"
              maxLength={255}
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              {nombreCustom.length}/255 caracteres
            </p>
          </div>

          {/* Categoría */}
          <div className="space-y-2">
            <Label htmlFor="tipo">Categoría *</Label>
            <Select
              value={tipo}
              onValueChange={(value) => setTipo(value as TipoDocumento)}
              disabled={isLoading}
            >
              <SelectTrigger id="tipo">
                <SelectValue placeholder="Seleccionar categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="proforma">Proforma</SelectItem>
                <SelectItem value="factura">Factura</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 border border-destructive/20">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? 'Guardando...' : isEditMode ? 'Guardar' : 'Agregar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
